"use client";

import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import Icon from "@/components/ui/AppIcon";

interface Service {
  icon: string;
  title: string;
  description: string;
  seoKeyword: string;
  highlight?: boolean;
}

const services: Service[] = [
  {
    icon: "HeartIcon",
    title: "Bedside Nursing Care",
    description: "Round-the-clock professional nursing at your home. Our trained nurses monitor vitals, administer medications, and provide clinical care with compassion.",
    seoKeyword: "Home nursing care Kampala",
    highlight: true,
  },
  {
    icon: "UserGroupIcon",
    title: "Elderly Care",
    description: "Dignified, respectful support for elderly family members — from daily routines to companionship, ensuring independence and quality of life.",
    seoKeyword: "Elderly care Naguru Kololo",
  },
  {
    icon: "BeakerIcon",
    title: "Medication Management",
    description: "Precise administration and scheduling of medications, with detailed logs for doctor reviews. Safe, accurate, and timely — always.",
    seoKeyword: "Medication management elderly Munyonyo",
  },
  {
    icon: "BandageIcon",
    title: "Wound Care & Dressing",
    description: "Professional wound assessment, cleaning, and dressing by qualified nurses. Preventing infection and promoting faster healing at home.",
    seoKeyword: "Wound care nursing Bugolobi Uganda",
  },
  {
    icon: "ArrowPathIcon",
    title: "Mobility & Physiotherapy Support",
    description: "Safe movement assistance, exercise routines, and mobility aids guidance to help patients regain strength and prevent deterioration.",
    seoKeyword: "Mobility care at home Buziga Kampala",
  },
  {
    icon: "SparklesIcon",
    title: "Personal Hygiene & Bathing",
    description: "Respectful assistance with bathing, grooming, and personal hygiene — maintaining dignity and comfort for patients who need support.",
    seoKeyword: "Personal hygiene nursing Nalya Uganda",
  },
  {
    icon: "ChartBarIcon",
    title: "Vital Signs Monitoring",
    description: "Regular monitoring of blood pressure, temperature, pulse, and oxygen levels with detailed reports for medical consultations.",
    seoKeyword: "Vital signs monitoring home Kampala",
  },
  {
    icon: "SunIcon",
    title: "Palliative & End-of-Life Care",
    description: "Compassionate comfort care for patients in the final stages of illness — focusing on pain relief, peace, and family support.",
    seoKeyword: "End-of-life care at home Kampala",
  },
];

export default function ServicesSection() {
  const sectionRef = useScrollAnimation<HTMLDivElement>();

  const staggerClasses = ['stagger-1','stagger-2','stagger-3','stagger-4','stagger-5','stagger-6','stagger-1','stagger-2'];

  return (
    <section
      id="services"
      ref={sectionRef}
      className="py-16 md:py-20 lg:py-24 xl:py-32"
      style={{ background: 'var(--cream)' }}
      aria-labelledby="services-heading"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-10 md:mb-12 lg:mb-16 reveal-up stagger-1">
          <span className="inline-block text-[10px] md:text-xs font-semibold tracking-widest uppercase font-sans mb-3 md:mb-4 px-3 md:px-4 py-1.5 md:py-2 rounded-full" style={{ color: 'var(--gold)', background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)' }}>
            Our Services
          </span>
          <h2 id="services-heading" className="font-serif text-3xl md:text-4xl lg:text-5xl font-light text-navy leading-tight mt-3 md:mt-4 mb-3 md:mb-5 px-2">
            Everything Your Loved One Needs,{" "}
            <span className="italic" style={{ color: 'var(--gold)' }}>At Home</span>
          </h2>
          <p className="text-muted text-sm md:text-base lg:text-lg max-w-2xl mx-auto leading-relaxed font-sans px-4" style={{ color: 'var(--muted)' }}>
            From daily nursing visits to round-the-clock palliative support — our qualified clinical team delivers hospital-grade care in the comfort of your home.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {services.map((service, i) => (
            <article
              key={`service-${service.title}`}
              className={`service-card p-5 md:p-6 reveal-up ${staggerClasses[i]} ${service.highlight ? 'sm:col-span-2' : ''}`}
              aria-label={service.title}
            >
              {/* SEO-friendly hidden keyword */}
              <span className="sr-only">{service.seoKeyword}</span>

              {service.highlight && (
                <div className="absolute top-3 md:top-4 right-3 md:right-4 px-2.5 md:px-3 py-1 rounded-full text-[10px] md:text-xs font-semibold font-sans" style={{ background: 'rgba(201,168,76,0.12)', color: 'var(--gold)', border: '1px solid rgba(201,168,76,0.25)' }}>
                  Most Requested
                </div>
              )}

              <div className="service-icon-wrap mb-4 md:mb-5">
                <Icon name={service.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-gold md:w-6 md:h-6" style={{ color: 'var(--gold)' }} />
              </div>

              <h3 className="font-serif text-lg md:text-xl font-medium text-navy mb-2 md:mb-3 leading-tight">
                {service.title}
              </h3>
              <p className="text-xs md:text-sm leading-relaxed font-sans" style={{ color: 'var(--muted)' }}>
                {service.description}
              </p>

              <a 
                href={`https://wa.me/256788217719?text=Hi%20Beecon,%20I'd%20like%20to%20learn%20more%20about%20${encodeURIComponent(service.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 md:mt-5 flex items-center gap-2 text-xs md:text-sm font-medium font-sans hover:opacity-80 transition-opacity"
                style={{ color: 'var(--gold)' }}
                aria-label={`Learn more about ${service.title} via WhatsApp`}
              >
                <span>Learn more</span>
                <Icon name="ArrowRightIcon" size={12} className="text-gold md:w-[14px] md:h-[14px]" />
              </a>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}